/**
 * GateRulesPage — PAGE-007, Admin only.
 * Orchestrates: rules list, create/edit dialog, delete confirmation.
 * Admin gate: primary enforcement is AdminRoute in App.tsx (role guard + redirect).
 * Secondary runtime check added here for belt-and-suspenders safety.
 *
 * FR-061, FR-072
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { RulesList } from './RulesList';
import { RuleFormDialog } from './RuleFormDialog';
import { RuleDeleteConfirm } from './RuleDeleteConfirm';
import type { GateRuleResponse } from '@/hooks/useGateRules';

export function GateRulesPage() {
  const { isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<GateRuleResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GateRuleResponse | null>(null);

  // Secondary runtime admin guard (primary is AdminRoute in App.tsx)
  if (!isLoading && !isAdmin) {
    toast.error('You do not have permission to manage gate rules.');
    navigate('/dashboard', { replace: true });
    return null;
  }

  const handleCreate = () => {
    setEditTarget(null);
    setFormOpen(true);
  };

  const handleEdit = (rule: GateRuleResponse) => {
    setEditTarget(rule);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditTarget(null);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gate Rules</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure the rules the Gate Agent uses to evaluate contracts during pipeline execution.
          </p>
        </div>
        <Button onClick={handleCreate} aria-label="Create a new gate rule">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Create Rule
        </Button>
      </div>

      {/* Rules table */}
      <RulesList
        onEdit={handleEdit}
        onDelete={setDeleteTarget}
        onCreate={handleCreate}
      />

      {/* Create / Edit dialog */}
      <RuleFormDialog
        open={formOpen}
        editTarget={editTarget}
        onClose={handleFormClose}
      />

      {/* Delete confirmation */}
      <RuleDeleteConfirm
        rule={deleteTarget}
        onClose={() => { setDeleteTarget(null); }}
      />
    </div>
  );
}
